import { RefetchQueriesInclude, useLazyQuery } from "@apollo/client";
import { DOCUMENT_UPLOAD_USER_ERROR_CODES } from "encamp-constants/documents";
import { INCOMPLETE_PDF_MESSAGE } from "encamp-shared/src/pdf/messages";
import { ensureFileExtension } from "encamp-shared/src/utils/ensureFileExtension";
import { gql } from "generated-graphql";
import {
  Document,
  DocumentAssociationsInput,
  DocumentInput2,
  UploadedDocumentQuery,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

import { uploadFileDirectly } from "../util/uploadFile";
import { useUpsertDocument } from "./useUpsertDocument";

const UPLOADED_DOCUMENT_QUERY = gql(`
  query UploadedDocument($id: ID!) {
    document(id: $id) {
      id
      tenantId
      documentType
      title
      fileExtension
      storageLink
      downloadLink
    }
  }
`);

const PDF_IS_LOADABLE_QUERY = gql(`
  query PdfIsLoadable($key: ID!, $tenantId: ID!) {
    pdfIsLoadable(key: $key, tenantId: $tenantId)
  }
`);

// Re-exported so the payment dialog can match the exact message it surfaces
// verbatim (rather than a fragile substring). PDF_UNVERIFIABLE_MESSAGE covers
// the case where the loadability check itself can't run (transient S3/network).
export { INCOMPLETE_PDF_MESSAGE };
export const PDF_UNVERIFIABLE_MESSAGE =
  "We couldn't verify this PDF right now. Please try attaching it again.";

// Re-exported so uploaders keep importing it from the hook they already use.
export { DOCUMENT_UPLOAD_USER_ERROR_CODES };

/**
 * Returns `file` renamed so its name carries `fileExtension`. The S3 key is
 * derived from the file name, but that name comes from the user-editable
 * Filename field, which can drop or mangle the extension (e.g. "Invoice v1.2"
 * keys as `.2`). Everything downstream — PDF conversion, file-type inference,
 * Lob mailing, the loadability check — keys off the extension, and this is the
 * single point every uploader flows through, keeping the write side in
 * agreement with the read side (getFileNameForDownload reconstructs it on
 * download).
 */
function withKeyExtension(file: File, fileExtension?: string | null): File {
  if (!fileExtension) {
    return file;
  }
  const correctedName = ensureFileExtension(file.name, fileExtension);
  return correctedName === file.name ? file : new File([file], correctedName, { type: file.type });
}

type CheckPdfLoadable = (opts: {
  variables: { key: string; tenantId: string };
}) => Promise<{ data?: { pdfIsLoadable?: boolean | null } | null; error?: unknown }>;

/**
 * Throws a user-facing message unless the object behind `key` is a loadable PDF.
 * Fails closed: a query/network/S3 error blocks rather than silently attaching
 * an unvalidated document. Runs whether the key was just uploaded or picked
 * from existing documents, so a corrupt invoice in S3 can't sail through to Lob.
 */
async function assertPdfLoadable(
  check: CheckPdfLoadable,
  key: string,
  tenantId: string
): Promise<void> {
  const { data, error } = await check({ variables: { key, tenantId } });
  if (data?.pdfIsLoadable === false) {
    throw new Error(INCOMPLETE_PDF_MESSAGE);
  }
  if (error || data?.pdfIsLoadable !== true) {
    throw new Error(PDF_UNVERIFIABLE_MESSAGE);
  }
}

/**
 * Hook for uploading a document and upserting it to the database
 * @returns
 */
export function useDocumentUpload(config?: {
  onSuccess?: (document: UploadedDocumentQuery["document"]) => void;
  onError?: (error: Error) => void;
  refetchQueries?: RefetchQueriesInclude;
  generateTitleOnCreate?: boolean;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [document, setDocument] = useState<UploadedDocumentQuery["document"] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const { upsertDocument } = useUpsertDocument({
    refetchQueries: config?.refetchQueries,
  });
  const [getDocument] = useLazyQuery(UPLOADED_DOCUMENT_QUERY);
  const [checkPdfLoadable] = useLazyQuery(PDF_IS_LOADABLE_QUERY);

  const uploadFile = useCallback(
    async ({
      file,
      documentData,
      currentSubmissionDocument,
      associations,
      validatePdf,
    }: {
      file?: File;
      documentData: Document | DocumentInput2;
      currentSubmissionDocument: boolean;
      associations?: DocumentAssociationsInput[];
      validatePdf?: boolean;
    }) => {
      setUploadComplete(false);
      setIsUploading(true);
      setDocument(null);
      setError(null);

      if (!documentData.tenantId) {
        throw new Error("Tenant ID is required");
      }

      try {
        let documentKey: string;
        if (file) {
          documentKey = await uploadFileDirectly(
            withKeyExtension(file, documentData.fileExtension),
            { tenantId: documentData.tenantId ?? "" }
          );
        } else {
          if (!documentData.storageLink) {
            throw new Error("Storage link is required");
          }

          documentKey = documentData.storageLink;
        }

        if (validatePdf) {
          await assertPdfLoadable(checkPdfLoadable, documentKey, documentData.tenantId);
        }

        const documentId = await upsertDocument({
          documentData: {
            ...documentData,
            storageLink: documentKey,
          },
          generateTitleOnCreate: config?.generateTitleOnCreate,
          associations,
          currentSubmissionDocument,
        });

        if (!documentId) {
          throw new Error("Document ID not found");
        }
        const document = await getDocument({
          variables: { id: documentId },
        });

        if (!document.data?.document) {
          throw new Error("Document not found");
        }

        setDocument(document.data.document);
        setUploadComplete(true);
        config?.onSuccess?.(document.data.document);
      } catch (err) {
        setError(err as Error);
        config?.onError?.(err as Error);
      } finally {
        setIsUploading(false);
      }
    },
    [upsertDocument, getDocument, checkPdfLoadable, config]
  );

  return useMemo(
    () => ({
      isUploading,
      uploadComplete,
      document,
      error,
      uploadFile,
    }),
    [isUploading, uploadComplete, document, error, uploadFile]
  );
}

export function useUploadFiles() {
  return useCallback(
    async (documentsWithMaybeFile: { file?: File; documentData: DocumentInput2 }[]) => {
      if (!documentsWithMaybeFile.every((doc) => doc.documentData.tenantId)) {
        throw new Error("Tenant ID is required for all documents");
      }
      return await Promise.all(
        documentsWithMaybeFile.map(({ file, documentData }) =>
          file
            ? uploadFileDirectly(file, {
                tenantId: documentData.tenantId ?? "",
              })
            : Promise.resolve(documentData.storageLink)
        )
      );
    },
    []
  );
}
